import { css } from "@emotion/react"
import { useIntl } from "react-intl"
import { colors, fonts, timings, backgrounds, BootIndexResultsScatterPlot } from "shared-ui"
import Modal from "../_layout/Modal"
import PitchTypeIcon from "../pitch/PitchTypeIcon"
import useRawState from "../../hooks/useRawState"
import useBootRatings from "../../hooks/useBootRatings"
import Loader from "../_general/Loader"
import { TestAverageLinearGauge } from "shared-ui"
import { NavLink } from "react-router"
import { lighten } from "polished"
import { useContext } from "react"
import UnitSystemContext from "../../context/UnitSystem"
import { useEffect, useState } from "react"
import useRawPromise from "../../hooks/useRawPromise"

export default function BootAdviceModal({ pitch, indexData, bootTypes, onClickClose, ...props }) {

    const { formatMessage } = useIntl()
    const { unitSystem } = useContext(UnitSystemContext)
    const { get } = useRawPromise()

    const index = indexData?.find(ind => ind.indexCategory.indexCategoryGroup === 'boot')
    if (!index) onClickClose()

    // get the ratings info of the index category of that boot index
    const { bootRatings: ratings, isBusy } = useBootRatings(index)

    const recommendedBoot = index.bootRecommendations[0]?.bootType || undefined

    const relatedAnalysisIdStr = index?.analysis?.split('/').pop() ?? ''
    const { data: analysis, isBusy: analbusy } = useRawState(
        relatedAnalysisIdStr ?
            `/api/current/surface-analyses/${relatedAnalysisIdStr}`
            : undefined
    )

    const testCategoriesToShow = index.metaData.map(md => md.test_category_id)

    // check if indexZoneResults are IRI or objects, fetch if necessary
    if (!index.indexZoneResults) return null
    const zoneResultsAreObjects = index.indexZoneResults.length > 0 && typeof index.indexZoneResults[0] === 'object'
    const [fetchedZoneResults, setFetchedZoneResults] = useState(null)

    useEffect(() => {
        if (zoneResultsAreObjects) return   // already objects
        const fetchZoneResults = async () => {
            try {
                const fetchedZoneResults = await get(index['@id'])
                setFetchedZoneResults(fetchedZoneResults.data.indexZoneResults)
            } catch (error) {
                console.error('Error fetching zone results: ', error)
            }
        }
        fetchZoneResults()
    }, [index, zoneResultsAreObjects])

    return <Modal
        header={
            <div title={pitch?.type} css={style.modal} >
                <PitchTypeIcon
                    pitch={pitch}
                    css={style.pitchTypeIcon}
                />
                <h2 css={style.title}>
                    {pitch?.name}
                </h2>
            </div>
        }
        children={
            <div css={css`width: 100%;`}>
                <h3 css={[css`margin-bottom: 0.875em;`, appearance.h3]}>
                    {formatMessage({ id: 'bootIndex' })}
                </h3>

                <BootIndexResultsScatterPlot
                    avg={index.value}
                    zoneResults={fetchedZoneResults ?? index.indexZoneResults}
                    ratings={ratings}
                    recommendedBoot={recommendedBoot}
                    bootTypes={bootTypes}
                    isBusy={isBusy}
                />

                <h3 css={[css`margin-bottom: 0.875em; margin-top: 1em;`, appearance.h3]}>
                    {formatMessage({ id: 'relatedTests' })}
                </h3>

                {analbusy ?
                    <div css={style.loaderWrapper}>
                        <Loader size={'3em'} />
                    </div>
                    :
                    <NavLink to={`/pitch/${pitch.id}/analysis/${relatedAnalysisIdStr}`}>
                        <div css={appearance.tests}>
                            {analysis?.tests
                                .filter(test => testCategoriesToShow.includes(test.testCategory.id))
                                .toSorted((a, b) => a.testCategory.weight - b.testCategory.weight)
                                .map(test => <TestAverageLinearGauge
                                    key={test.id}
                                    test={test}
                                    unitSystem={unitSystem}
                                    lightMode={false}
                                    notApplicableAbbrString={formatMessage({ id: 'notApplicableAbbr' })}
                                />)
                            }
                        </div>
                    </NavLink>
                }
            </div>
        }
        onClickClose={onClickClose}
        {...props}
    />
}

const style = {
    title: css`
        display: inline-block;
        font-size: 1.2em;
        line-height: 1.2;
        font-family: ${fonts.special};
        font-weight: 700;
        text-transform: uppercase;
    `,
    modal: css`
        display: flex; 
        align-items: center; 
        gap: 1em;
        
        @media screen and (max-width: 600px) {
            font-size: 0.75em;
        }
    `,
    loaderWrapper: css`
        width: 100%; 
        display: flex; 
        justify-content: center;
    `,
    pitchTypeIcon: css`
        height: 2em;
        width: 2em;
        @media screen and (max-width: 600px) {
            display: none;
        }
    `,
}

const appearance = {
    h3: css`
        font-size: 1.75em;
        font-weight: 200;
        line-height: 1;
        font-family: ${fonts.special};
        color: ${colors.subtle};
        `,
    tests: css`
        background: ${colors.eventDark};
        border-radius: 5px;
        transition: background ${timings.smooth} ease-in-out;
        background-image: ${backgrounds.cardGradient};

        &:hover {
            background: ${lighten(0.05, colors.eventDark)};
            background-image: ${backgrounds.cardGradient};
        }
    `,
}

